import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spin, Empty, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { queries } from 'services/queries';
import {
  updateOfficialScenario,
  updateOfficialScenarioPolicy,
  updateOfficialScenarioSubPolicy,
} from 'services/api/pureLLM';
import { useQuery as useRouterQuery } from 'components/RouterQuery';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useQaContext } from 'views/Aide/Qa/Context';
import useModal from 'hooks/useModal';
import Header from './Components/Header';
import CategoryFilter from './Components/CategoryFilter';
import ScenarioGroupList from './Components/ScenarioGroupList';
import DetailHeader from './Components/ScenarioDetail/DetailHeader';
import RuleStatusBar from './Components/ScenarioDetail/RuleStatusBar';
import BranchCard from './Components/ScenarioDetail/BranchCard';
import type { BranchCardData, BranchCardProps } from './Components/ScenarioDetail/BranchCard';
import OperationLogDrawer from './Components/ScenarioDetail/OperationLogDrawer';
import FeedbackModal from './Components/ScenarioDetail/FeedbackModal';
import ReceptionModeDrawer from './Components/ScenarioDetail/ReceptionModeDrawer';
import {
  mapCategories,
  mapPolicies,
  filterScenariosByCategory,
  filterScenarioGroupsByField,
  sortScenarioGroups,
} from '../Hooks/useOfficialScenarioData';
import { useResolveTodoTypeLabel } from '../Hooks/useResolveTodoTypeLabel';
import type { IntentGroup, Scenario } from '../Types/api';

interface FeedbackMeta {
  presetReply: string;
}

interface ReceptionModeMeta {
  subPolicyId?: string;
  branchName: string;
  triggerConditions: BranchCardProps['triggerConditions'];
  replyLogicDesc: string;
  replyExample: string;
  fixedReplyContent?: string;
  isFixedReply: boolean;
  currentMode: BranchCardProps['receptionMode'];
  originalReceptionMode?: BranchCardProps['originalReceptionMode'];
  todoLabel?: string;
  todoType?: string;
  storeScope?: BranchCardProps['storeScope'];
}

function normalizeScenarioId(id: string | number) {
  if (typeof id === 'number') return id;
  return /^\d+$/.test(id) ? Number(id) : id;
}

function clampCount(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function updateGroupsByScenarioEnable(
  groups: IntentGroup[] | undefined,
  intentId: string,
  enable: boolean,
): IntentGroup[] | undefined {
  return groups?.map((group) => ({
    ...group,
    scenario: group.scenario.map((item) =>
      String(item.intentId) === intentId
        ? {
            ...item,
            enable,
          }
        : item,
    ),
  }));
}

function updateGroupsByPolicyEnable(
  groups: IntentGroup[] | undefined,
  intentId: string,
  policyId: string,
  enable: boolean,
): IntentGroup[] | undefined {
  return groups?.map((group) => ({
    ...group,
    scenario: group.scenario.map((scenario) => {
      if (String(scenario.intentId) !== intentId) {
        return scenario;
      }

      let hasMatchedPolicy = false;
      const policyGroup = scenario.policyGroup?.map((groupItem) => ({
        ...groupItem,
        policy: groupItem.policy.map((policy) => {
          if (String(policy.policyId) !== policyId) {
            return policy;
          }

          hasMatchedPolicy = true;

          return {
            ...policy,
            enable,
          };
        }),
      }));

      if (policyGroup?.length && hasMatchedPolicy) {
        const enabledCount = policyGroup.reduce(
          (sum, groupItem) => sum + groupItem.policy.filter((policy) => policy.enable).length,
          0,
        );

        return {
          ...scenario,
          policyGroup,
          enablePolicyCount: String(enabledCount),
        };
      }

      const previousEnabledCount = Number(scenario.enablePolicyCount ?? 0);
      const totalCount = Number(scenario.policyCount ?? previousEnabledCount);
      const nextEnabledCount = clampCount(previousEnabledCount + (enable ? 1 : -1), 0, totalCount);

      return {
        ...scenario,
        enablePolicyCount: String(nextEnabledCount),
      };
    }),
  }));
}

function updateScenarioDetailByPolicyEnable(
  scenario: Scenario | undefined,
  policyId: string,
  enable: boolean,
): Scenario | undefined {
  if (!scenario?.policyGroup?.length) {
    return scenario;
  }

  let hasMatchedPolicy = false;

  const policyGroup = scenario.policyGroup.map((groupItem) => ({
    ...groupItem,
    policy: groupItem.policy.map((policy) => {
      if (String(policy.policyId) !== policyId) {
        return policy;
      }

      hasMatchedPolicy = true;

      return {
        ...policy,
        enable,
      };
    }),
  }));

  if (!hasMatchedPolicy) {
    return scenario;
  }

  const enabledCount = policyGroup.reduce(
    (sum, groupItem) => sum + groupItem.policy.filter((policy) => policy.enable).length,
    0,
  );

  return {
    ...scenario,
    policyGroup,
    enablePolicyCount: String(enabledCount),
  };
}

function OfficialScenario() {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const operationLogDrawer = useModal();
  const feedbackModal = useModal<FeedbackMeta>();
  const receptionModeDrawer = useModal<ReceptionModeMeta>();

  const [activeCategory, setActiveCategory] = useState('all');
  const [activeScenarioId, setActiveScenarioId] = useState<string>();
  const [togglingScenarioIds, setTogglingScenarioIds] = useState<string[]>([]);
  const [togglingPolicyIds, setTogglingPolicyIds] = useState<string[]>([]);

  const { region, storeGroupId, storeGroupList, storeGroupListLoading } = useQaContext();
  const routerQuery = useRouterQuery();
  const language = useSystemLanguageValue();

  const searchKeyword = (routerQuery.keyword as string) || undefined;

  const queryParams = useMemo(
    () => ({
      region,
      storeGroupId,
      scenarioEnableStatus: routerQuery.scenarioEnableStatus as string | undefined,
      keyword: searchKeyword,
      language,
    }),
    [region, storeGroupId, routerQuery.scenarioEnableStatus, searchKeyword, language],
  );
  const hasStoreGroupId = storeGroupId !== '' && storeGroupList.length > 0;

  const scenarioListQuery = queries.pureLLM.scenarioList(queryParams);
  const {
    data: intentGroups,
    isLoading: _isLoading,
    isError,
  } = useQuery({
    ...scenarioListQuery,
    enabled: hasStoreGroupId,
  });
  const isLoading = hasStoreGroupId && _isLoading;
  const hasActiveScenario = activeScenarioId !== undefined;
  const activeScenarioDetailQuery = queries.pureLLM.scenarioDetail({
    region,
    storeGroupId,
    scenarioEnableStatus: routerQuery.scenarioEnableStatus as string | undefined,
    keyword: searchKeyword,
    language,
    intentId: activeScenarioId ?? '',
  });
  const {
    data: activeScenario,
    isLoading: _isDetailLoading,
    isFetching: _isDetailFetching,
    isError: isDetailError,
  } = useQuery({
    ...activeScenarioDetailQuery,
    enabled: hasStoreGroupId && hasActiveScenario,
  });
  const isDetailLoading = hasStoreGroupId && hasActiveScenario && _isDetailLoading;
  const isDetailFetching = hasStoreGroupId && hasActiveScenario && _isDetailFetching;

  // 筛选条件变化时重置 activeScenarioId，确保 list 先请求、detail 后用最新的 intentId 再请求
  const prevFilterParamsRef = useRef({
    region,
    storeGroupId,
    scenarioEnableStatus: routerQuery.scenarioEnableStatus as string | undefined,
  });

  useEffect(() => {
    const prev = prevFilterParamsRef.current;
    const cur = {
      region,
      storeGroupId,
      scenarioEnableStatus: routerQuery.scenarioEnableStatus as string | undefined,
    };
    if (
      prev.region !== cur.region ||
      prev.storeGroupId !== cur.storeGroupId ||
      prev.scenarioEnableStatus !== cur.scenarioEnableStatus
    ) {
      setActiveScenarioId(undefined);
    }
    prevFilterParamsRef.current = cur;
  }, [region, storeGroupId, routerQuery.scenarioEnableStatus]);

  useEffect(() => {
    if (!isError) return;

    message.error(t('scene.aiAgentConfig.feedback.fetchFailed'));
  }, [isError, t]);

  useEffect(() => {
    if (!isDetailError) return;

    message.error(t('scene.aiAgentConfig.feedback.fetchFailed'));
  }, [isDetailError, t]);

  const scenarioToggleMutation = useMutation({
    mutationFn: ({ intentId, enable }: { intentId: string; enable: boolean }) =>
      updateOfficialScenario({
        region,
        storeGroupId: normalizeScenarioId(storeGroupId),
        intentId: normalizeScenarioId(intentId),
        enable,
      }),
    onMutate: async ({ intentId, enable }) => {
      const scenarioId = String(intentId);
      const detailQueryKey = hasActiveScenario ? activeScenarioDetailQuery.queryKey : undefined;

      setTogglingScenarioIds((prev) => (prev.includes(scenarioId) ? prev : [...prev, scenarioId]));

      await queryClient.cancelQueries({
        queryKey: scenarioListQuery.queryKey,
      });

      const previousGroups = queryClient.getQueryData<IntentGroup[]>(scenarioListQuery.queryKey);
      const previousScenarioDetail = detailQueryKey
        ? queryClient.getQueryData<Scenario>(detailQueryKey)
        : undefined;

      queryClient.setQueryData<IntentGroup[]>(scenarioListQuery.queryKey, (oldGroups) =>
        updateGroupsByScenarioEnable(oldGroups, scenarioId, enable),
      );
      if (detailQueryKey && scenarioId === activeScenarioId) {
        queryClient.setQueryData<Scenario | undefined>(detailQueryKey, (oldData) =>
          oldData
            ? {
                ...oldData,
                enable,
              }
            : oldData,
        );
      }

      return {
        detailQueryKey,
        previousGroups,
        previousScenarioDetail,
        scenarioId,
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousGroups) {
        queryClient.setQueryData(scenarioListQuery.queryKey, context.previousGroups);
      }
      if (context?.detailQueryKey && context.previousScenarioDetail) {
        queryClient.setQueryData(context.detailQueryKey, context.previousScenarioDetail);
      }

      message.error(t('scene.aiAgentConfig.feedback.operationFailed'));
    },
    onSuccess: () => {
      message.success(t('scene.aiAgentConfig.feedback.operationSuccess'));
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.scenarioId) {
        setTogglingScenarioIds((prev) => prev.filter((id) => id !== context.scenarioId));
      }
    },
  });

  const policyToggleMutation = useMutation({
    mutationFn: ({
      intentId,
      policyId,
      enable,
    }: {
      intentId: string;
      policyId: string;
      enable: boolean;
    }) =>
      updateOfficialScenarioPolicy({
        region,
        storeGroupId: normalizeScenarioId(storeGroupId),
        policyId: normalizeScenarioId(policyId),
        enable,
      }),
    onMutate: async ({ policyId, intentId, enable }) => {
      const normalizedPolicyId = String(policyId);
      const normalizedIntentId = String(intentId);
      const detailQueryKey = hasActiveScenario ? activeScenarioDetailQuery.queryKey : undefined;

      setTogglingPolicyIds((prev) =>
        prev.includes(normalizedPolicyId) ? prev : [...prev, normalizedPolicyId],
      );

      await queryClient.cancelQueries({
        queryKey: scenarioListQuery.queryKey,
      });
      if (detailQueryKey) {
        await queryClient.cancelQueries({
          queryKey: detailQueryKey,
        });
      }

      const previousGroups = queryClient.getQueryData<IntentGroup[]>(scenarioListQuery.queryKey);
      const previousScenarioDetail = detailQueryKey
        ? queryClient.getQueryData<Scenario>(detailQueryKey)
        : undefined;

      queryClient.setQueryData<IntentGroup[]>(scenarioListQuery.queryKey, (oldGroups) =>
        updateGroupsByPolicyEnable(oldGroups, normalizedIntentId, normalizedPolicyId, enable),
      );
      if (detailQueryKey && normalizedIntentId === activeScenarioId) {
        queryClient.setQueryData<Scenario | undefined>(detailQueryKey, (oldData) =>
          updateScenarioDetailByPolicyEnable(oldData, normalizedPolicyId, enable),
        );
      }

      return {
        detailQueryKey,
        previousGroups,
        previousScenarioDetail,
        intentId: normalizedIntentId,
        policyId: normalizedPolicyId,
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousGroups) {
        queryClient.setQueryData(scenarioListQuery.queryKey, context.previousGroups);
      }
      if (context?.detailQueryKey && context.previousScenarioDetail) {
        queryClient.setQueryData(context.detailQueryKey, context.previousScenarioDetail);
      }

      message.error(t('scene.aiAgentConfig.feedback.operationFailed'));
    },
    onSuccess: () => {
      message.success(t('scene.aiAgentConfig.feedback.operationSuccess'));
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.policyId) {
        setTogglingPolicyIds((prev) => prev.filter((id) => id !== context.policyId));
      }
    },
  });

  const receptionModeMutation = useMutation({
    mutationFn: ({
      subPolicyId,
      mode,
      todoType,
    }: {
      subPolicyId: string;
      mode: NonNullable<ReceptionModeMeta['currentMode']>;
      todoType?: string;
    }) =>
      updateOfficialScenarioSubPolicy({
        region,
        storeGroupId: normalizeScenarioId(storeGroupId),
        subPolicyId: normalizeScenarioId(subPolicyId),
        receptionMode: mode,
        todoType: todoType ?? '',
      }),
    onSuccess: () => {
      message.success(t('scene.aiAgentConfig.feedback.operationSuccess'));
      if (hasActiveScenario) {
        queryClient.invalidateQueries({
          queryKey: activeScenarioDetailQuery.queryKey,
        });
      }
    },
    onError: () => {
      message.error(t('scene.aiAgentConfig.feedback.operationFailed'));
    },
  });

  const groups = intentGroups as IntentGroup[] | undefined;

  // 分类标签
  const categories = useMemo(() => (groups ? mapCategories(groups, t) : undefined), [groups, t]);
  const resolveTodoTypeLabel = useResolveTodoTypeLabel();

  // 左侧场景列表
  const scenarioList = useMemo(() => {
    if (!groups) return undefined;

    const categoryFilteredGroups = filterScenariosByCategory(groups, activeCategory);
    const searchFilteredGroups = filterScenarioGroupsByField(
      categoryFilteredGroups,
      (routerQuery.sortBy as string | undefined) || (routerQuery.searchField as string | undefined),
      undefined,
    );

    return sortScenarioGroups(searchFilteredGroups, routerQuery.sortBy as string | undefined);
  }, [groups, activeCategory, routerQuery.searchField, routerQuery.sortBy]);

  // 切换分类 / 筛选条件时，自动选中第一个场景
  useEffect(() => {
    if (scenarioList?.length) {
      const inList = scenarioList.some((s) => s.id === activeScenarioId);
      if (!inList) {
        setActiveScenarioId(scenarioList[0].id);
      }
      return;
    }

    if (activeScenarioId !== undefined) {
      setActiveScenarioId(undefined);
    }
  }, [activeScenarioId, scenarioList]); // eslint-disable-line react-hooks/exhaustive-deps

  // 右侧分支卡片
  const branchCards: BranchCardData[] | undefined = useMemo(
    () => (activeScenario ? mapPolicies(activeScenario, resolveTodoTypeLabel) : undefined),
    [activeScenario, resolveTodoTypeLabel],
  );
  const currentStoreGroupName = useMemo(
    () => storeGroupList.find((item) => `${item.id}` === storeGroupId)?.name || '',
    [storeGroupId, storeGroupList],
  );

  const branchCardsWithActions = useMemo(() => {
    const attachBranchCardActions = (item: BranchCardData): BranchCardData => ({
      ...item,
      searchKeyword,
      onFeedbackClick: item.replyExample
        ? () =>
            feedbackModal.show({
              presetReply: item.replyExample,
            })
        : undefined,
      onModifyReceptionMode: item.receptionMode
        ? () =>
            receptionModeDrawer.show({
              subPolicyId: item.togglePolicyId,
              branchName: item.title,
              triggerConditions: item.policyTriggerConditions ?? item.triggerConditions,
              replyLogicDesc: item.replyLogicDesc,
              replyExample: item.replyExample,
              fixedReplyContent: item.fixedReplyContent,
              isFixedReply: item.isFixedReply || false,
              currentMode: item.receptionMode,
              originalReceptionMode: item.originalReceptionMode,
              todoLabel: item.todoLabel,
              todoType: item.todoType,
              storeScope: item.storeScope,
            })
        : undefined,
      onToggle: item.policyId
        ? (enable) =>
            policyToggleMutation.mutate({
              intentId: String(activeScenario?.intentId ?? activeScenarioId ?? ''),
              policyId: String(item.policyId),
              enable,
            })
        : undefined,
      toggleLoading: item.policyId ? togglingPolicyIds.includes(String(item.policyId)) : false,
      children: item.children?.map(attachBranchCardActions),
    });

    return branchCards?.map(attachBranchCardActions);
  }, [
    activeScenario?.intentId,
    activeScenarioId,
    branchCards,
    feedbackModal,
    policyToggleMutation,
    receptionModeDrawer,
    searchKeyword,
    togglingPolicyIds,
  ]);

  // 详情头部数据
  const detailHeaderData = useMemo(
    () =>
      activeScenario
        ? {
            category: activeScenario.groupName,
            name: activeScenario.name,
            updateTime: activeScenario.updateTime,
            suggestedQuestions: activeScenario.example ?? [],
          }
        : undefined,
    [activeScenario],
  );

  // 状态栏数据
  const ruleStatusBarData = useMemo(
    () =>
      activeScenario
        ? {
            enabledCount: Number(activeScenario.enablePolicyCount),
            totalCount: Number(activeScenario.policyCount),
          }
        : undefined,
    [activeScenario],
  );

  const handleScenarioToggle = (intentId: string, enable: boolean) => {
    scenarioToggleMutation.mutate({
      intentId,
      enable,
    });
  };

  const isScenarioEmpty = !isLoading && (!scenarioList || scenarioList.length === 0);
  const isScenarioDetailLoading =
    Boolean(activeScenarioId) && (isDetailLoading || (isDetailFetching && !activeScenario));

  return (
    <div data-sid="os-root-01" className="flex flex-col h-full bg-white rounded-sm overflow-hidden">
      <Header />
      <CategoryFilter
        categories={categories}
        activeKey={activeCategory}
        onChange={setActiveCategory}
        loading={isLoading || storeGroupListLoading}
      />
      <div data-sid="os-main-02" className="flex flex-1 overflow-hidden">
        {storeGroupListLoading ? (
          <div data-sid="os-loading-08" className="flex flex-1 items-center justify-center">
            <Spin />
          </div>
        ) : storeGroupList.length === 0 ? (
          <div data-sid="os-empty-09" className="flex flex-1 items-center justify-center">
            <Empty description={t('scene.aiAgentConfig.scenarioList.empty.noData')} />
          </div>
        ) : isScenarioEmpty ? (
          <div data-sid="os-empty-09" className="flex flex-1 items-center justify-center">
            <Empty description={t('scene.aiAgentConfig.scenarioList.empty.noData')} />
          </div>
        ) : (
          <>
            <div
              data-sid="os-left-03"
              className="min-w-[242px] max-w-[280px] flex-[0.27] overflow-y-auto"
              style={{ borderRight: '1px solid #F0F2F5' }}
            >
              <ScenarioGroupList
                groups={scenarioList}
                activeId={activeScenarioId}
                keyword={searchKeyword}
                onSelect={setActiveScenarioId}
                onToggle={handleScenarioToggle}
                togglingIds={togglingScenarioIds}
              />
            </div>
            <div data-sid="os-right-04" className="flex-1 overflow-y-auto bg-[#fff]">
              {isLoading || isScenarioDetailLoading ? (
                <div data-sid="os-loading-08" className="flex items-center justify-center h-full">
                  <Spin />
                </div>
              ) : !detailHeaderData ? (
                <div
                  data-sid="os-placeholder-10"
                  className="flex items-center justify-center h-full text-[#84868C] text-sm"
                >
                  {t('scene.aiAgentConfig.scenarioList.placeholder.selectScenario')}
                </div>
              ) : (
                <>
                  <DetailHeader
                    {...detailHeaderData}
                    searchKeyword={searchKeyword}
                    onOperationLogClick={operationLogDrawer.show}
                  />
                  <div data-sid="os-branch-wrap-06" className="bg-[#fcfcfc]">
                    {ruleStatusBarData && <RuleStatusBar {...ruleStatusBarData} />}
                    <div data-sid="os-cards-05" className="px-4 pb-4">
                      {branchCardsWithActions?.map((item) => (
                        <BranchCard key={`${item.policyId}-${item.index}`} {...item} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <OperationLogDrawer
        visible={operationLogDrawer.visible}
        onClose={operationLogDrawer.hide}
        region={region}
        storeGroupId={storeGroupId}
        intentId={activeScenarioId}
        language={language}
      />

      <FeedbackModal
        visible={feedbackModal.visible}
        onClose={feedbackModal.hide}
        presetReply={feedbackModal.meta?.presetReply || ''}
      />

      <ReceptionModeDrawer
        visible={receptionModeDrawer.visible}
        onClose={receptionModeDrawer.hide}
        branchName={receptionModeDrawer.meta?.branchName || ''}
        triggerConditions={receptionModeDrawer.meta?.triggerConditions || []}
        replyLogicDesc={receptionModeDrawer.meta?.replyLogicDesc || ''}
        replyExample={receptionModeDrawer.meta?.replyExample || ''}
        fixedReplyContent={receptionModeDrawer.meta?.fixedReplyContent}
        isFixedReply={receptionModeDrawer.meta?.isFixedReply}
        currentMode={receptionModeDrawer.meta?.currentMode || 'AI_REPLY'}
        originalReceptionMode={receptionModeDrawer.meta?.originalReceptionMode}
        currentTodoType={receptionModeDrawer.meta?.todoType}
        storeGroupName={currentStoreGroupName}
        storeScope={receptionModeDrawer.meta?.storeScope}
        onSave={async (values) => {
          const subPolicyId = receptionModeDrawer.meta?.subPolicyId;
          if (!subPolicyId) {
            throw new Error('missing subPolicyId');
          }

          await receptionModeMutation.mutateAsync({
            subPolicyId,
            mode: values.mode,
            todoType: values.todoType,
          });
        }}
      />
    </div>
  );
}

export default OfficialScenario;
